import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAppointmentTypeClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import type {
  CreateAppointmentTypeRequest,
  UpdateAppointmentTypeRequest,
  UpsertOverrideRequest,
} from '@vagusx/clients';

/**
 * Everything a change to the type catalog invalidates.
 *
 * The appointment-type MATCHER reads types and eligibility, so editing,
 * deleting, or overriding a type changes which types and providers a referral
 * resolves to. Those answers are cached for ten minutes, and until this existed
 * nothing evicted them: disabling `Split Night` and going to the Referrals
 * worklist still showed it recommended — for ten minutes, with no way to tell
 * the screen was stale. Reported as "why is it picking a type I disabled?",
 * which is exactly how it looks.
 *
 * `useUpdateAppointmentType` already carried the comment "triggers eligibility
 * rebuild on BE" — the server was right, only the client's view was stale.
 */
function invalidateTypeDerivedCaches(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.appointmentTypes.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.appointmentTypeMatching.all });
}

/**
 * Hook to fetch all appointment types for the organization.
 */
export function useAppointmentTypes() {
  return useQuery({
    queryKey: queryKeys.appointmentTypes.list(),
    queryFn: () => getAppointmentTypeClient().list(),
  });
}

/**
 * Hook to create a new appointment type.
 */
export function useCreateAppointmentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAppointmentTypeRequest) => getAppointmentTypeClient().create(data),
    onSuccess: () => {
      invalidateTypeDerivedCaches(queryClient);
    },
  });
}

/**
 * Hook to update an appointment type (triggers eligibility rebuild on BE).
 */
export function useUpdateAppointmentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAppointmentTypeRequest }) =>
      getAppointmentTypeClient().update(id, data),
    onSuccess: () => {
      invalidateTypeDerivedCaches(queryClient);
    },
  });
}

/**
 * Hook to fetch all overrides for a specific appointment type.
 * Used in the "Provider Overrides" summary section of the type detail view.
 */
export function useAppointmentTypeOverrides(appointmentTypeId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.appointmentTypes.overrides(appointmentTypeId ?? ''),
    queryFn: () => getAppointmentTypeClient().getOverrides(appointmentTypeId!),
    enabled: !!appointmentTypeId,
  });
}

/**
 * Hook to fetch overrides for a provider, optionally scoped to a location.
 * Used in the provider view to show per-type override cards.
 */
export function useOverridesByProvider(providerId: string | undefined, locationId?: string) {
  return useQuery({
    queryKey: queryKeys.appointmentTypes.overridesByProvider(providerId ?? '', locationId),
    queryFn: () => getAppointmentTypeClient().getOverridesByProvider(providerId!, locationId),
    enabled: !!providerId,
  });
}

/**
 * Hook to delete (soft-delete) an appointment type.
 */
export function useDeleteAppointmentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getAppointmentTypeClient().remove(id),
    onSuccess: () => {
      invalidateTypeDerivedCaches(queryClient);
    },
  });
}

/**
 * Hook to upsert an override (triggers eligibility rebuild on BE).
 */
export function useUpsertOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpsertOverrideRequest) => getAppointmentTypeClient().upsertOverride(data),
    onSuccess: () => {
      invalidateTypeDerivedCaches(queryClient);
    },
  });
}
